const EPIC_PITY_TARGET = 20;
const LEGENDARY_PITY_TARGET = 90;

const styles = `
  :host {
    display: block;
  }

  .wrap {
    display: grid;
    gap: 10px;
  }

  .row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    color: rgba(247, 239, 218, 0.72);
    font-size: 0.78rem;
  }

  .row strong {
    color: #fff8e8;
    font: 900 0.9rem/1 var(--font-display, "Sora", sans-serif);
  }

  .dots {
    display: grid;
    grid-template-columns: repeat(var(--limit), minmax(0, 1fr));
    gap: 6px;
  }

  .dot {
    height: 12px;
    border-radius: 999px;
    background: rgba(247, 239, 218, 0.1);
    border: 1px solid rgba(247, 239, 218, 0.08);
    overflow: hidden;
  }

  .dot::before {
    content: "";
    display: block;
    width: 100%;
    height: 100%;
    transform: scaleX(0);
    transform-origin: left;
    background: linear-gradient(90deg, #a7f5ce, #68c7ff, #f5c76b);
  }

  .dot[data-filled='true']::before {
    transform: scaleX(1);
  }

  .pity {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }

  .meter {
    min-width: 0;
    display: grid;
    gap: 7px;
    padding: 9px;
    border-radius: 8px;
    background: rgba(247, 239, 218, 0.055);
    border: 1px solid rgba(247, 239, 218, 0.09);
  }

  .meter-top {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    color: rgba(247, 239, 218, 0.72);
    font-size: 0.68rem;
  }

  .meter-top strong {
    color: #fff8e8;
    font: 900 0.72rem/1 var(--font-display, "Sora", sans-serif);
  }

  .track {
    height: 7px;
    border-radius: 999px;
    overflow: hidden;
    background: rgba(247, 239, 218, 0.1);
  }

  .bar {
    display: block;
    width: var(--progress);
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, var(--start), var(--end));
  }

  @media (max-width: 374px) {
    .pity {
      grid-template-columns: 1fr;
    }
  }

  @media (prefers-reduced-motion: no-preference) {
    .dot[data-filled='true']::before,
    .bar {
      transition: width 260ms ease, transform 260ms ease;
    }
  }
`;

export class DailyProgressElement extends HTMLElement {
  private readonly root: ShadowRoot;
  private value = 0;
  private limit = 10;
  private label = 'packs';
  private legendaryPity = 0;
  private epicPity = 0;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  connectedCallback(): void {
    this.render();
  }

  setState(state: {
    value: number;
    limit: number;
    label?: string;
    legendaryPity: number;
    epicPity: number;
  }): void {
    this.value = state.value;
    this.limit = Math.max(1, state.limit);
    this.label = state.label ?? 'packs';
    this.legendaryPity = state.legendaryPity;
    this.epicPity = state.epicPity;
    this.render();
  }

  private render(): void {
    const dots = Array.from({ length: this.limit }, (_, index) => {
      const filled = index < this.value;
      return `<span class="dot" data-filled="${filled}"></span>`;
    }).join('');
    const epicProgress = percent(this.epicPity, EPIC_PITY_TARGET);
    const legendaryProgress = percent(this.legendaryPity, LEGENDARY_PITY_TARGET);

    this.root.innerHTML = `
      <style>${styles}</style>
      <div class="wrap" style="--limit: ${this.limit}">
        <div class="row">
          <span>Daily field packs</span>
          <strong>${this.value}/${this.limit} ${this.label}</strong>
        </div>
        <div class="dots" aria-label="${this.value} of ${this.limit} daily packs used">${dots}</div>
        <div class="pity">
          <div class="meter">
            <div class="meter-top">
              <span>Epic pity</span>
              <strong>${this.epicPity}/${EPIC_PITY_TARGET}</strong>
            </div>
            <div class="track"><span class="bar" style="--progress: ${epicProgress}%; --start: #e16f9d; --end: #f5c76b"></span></div>
          </div>
          <div class="meter">
            <div class="meter-top">
              <span>Legendary pity</span>
              <strong>${this.legendaryPity}/${LEGENDARY_PITY_TARGET}</strong>
            </div>
            <div class="track"><span class="bar" style="--progress: ${legendaryProgress}%; --start: #68c7ff; --end: #f5c76b"></span></div>
          </div>
        </div>
      </div>
    `;
  }
}

if (!customElements.get('daily-progress')) {
  customElements.define('daily-progress', DailyProgressElement);
}

function percent(value: number, target: number): number {
  return Math.max(0, Math.min(100, Math.round((value / target) * 100)));
}
